import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, forwardRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { EMPTY, debounceTime, switchMap, take, tap } from 'rxjs';

import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

import { Partner, PartnerLocation, PartnerLocationsSearchPayload, PartnerSearchPayload } from '@hm/core/models/partner.interface';
import { VisitsService } from '@hm/core/services/visits.service';

import { ModalActionTypes } from '@lib/core/models/modal-action-types.enum';
import { PagedResponse } from '@lib/core/models/paged-response';
import { AppointmentServiceCode, PrescriptionTypes } from '@lib/core/models/prescription.interface';
import { AppointmentEventPlaceType, Visit, VisitStatusTypes } from '@lib/core/models/visit.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

enum AppointmentControlsNames {
  PrescriptionsCodes = 'prescriptionsCodes',
  // TODO: Remove next line when sure that prescriptions ids array is obsolete.
  PrescriptionIds = 'prescriptionIds',
  AppointmentType = 'appointmentType',
  Name = 'name',
  Price = 'price',
  DoctorName = 'doctorName',
  DateStart = 'dateStart',
  Time = 'time',
  PartnerId = 'partnerId',
  PartnerName = 'partnerName',
  LocationId = 'locationId',
  Location = 'location',
  AppointmentEventPlaceType = 'appointmentEventPlaceType',
  City = 'city',
  AppointmentCompletedFlag = 'appointmentCompletedFlag',
}

interface AppointmentControls {
  [AppointmentControlsNames.PrescriptionsCodes]: FormControl<AppointmentServiceCode[]>;
  // TODO: Remove next line when sure that prescriptions ids array is obsolete.
  [AppointmentControlsNames.PrescriptionIds]: FormControl<string[]>;
  [AppointmentControlsNames.AppointmentType]: FormControl<PrescriptionTypes | null>;
  [AppointmentControlsNames.Name]: FormControl<string | null>;
  [AppointmentControlsNames.Price]: FormControl<number | null>;
  [AppointmentControlsNames.DoctorName]: FormControl<string | null>;
  [AppointmentControlsNames.DateStart]: FormControl<Date | null>;
  [AppointmentControlsNames.Time]: FormControl<string | null>;
  [AppointmentControlsNames.PartnerId]: FormControl<string | null>;
  [AppointmentControlsNames.PartnerName]: FormControl<string | null>;
  [AppointmentControlsNames.LocationId]: FormControl<string | null>;
  [AppointmentControlsNames.Location]: FormControl<string | null>;
  [AppointmentControlsNames.AppointmentEventPlaceType]: FormControl<AppointmentEventPlaceType | null>;
  [AppointmentControlsNames.City]: FormControl<string | null>;
  [AppointmentControlsNames.AppointmentCompletedFlag]: FormControl<boolean | null>;
}

interface AppointmentValue {
  [AppointmentControlsNames.PrescriptionsCodes]: AppointmentServiceCode[];
  [AppointmentControlsNames.PrescriptionIds]: string[];
  [AppointmentControlsNames.AppointmentType]: PrescriptionTypes | null;
  [AppointmentControlsNames.Name]: string | null;
  [AppointmentControlsNames.Price]: number | null;
  [AppointmentControlsNames.DoctorName]: string | null;
  [AppointmentControlsNames.DateStart]: Date | null;
  [AppointmentControlsNames.Time]: string | null;
  [AppointmentControlsNames.PartnerId]: string | null;
  [AppointmentControlsNames.PartnerName]: string | null;
  [AppointmentControlsNames.LocationId]: string | null;
  [AppointmentControlsNames.Location]: string | null;
  [AppointmentControlsNames.AppointmentEventPlaceType]: AppointmentEventPlaceType | null;
  [AppointmentControlsNames.City]: string | null;
  [AppointmentControlsNames.AppointmentCompletedFlag]: boolean | null;
}

@Component({
  selector: 'hm-manual-appointment-form',
  imports: [
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatCheckbox,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatInput,
    MatLabel,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    MatOptionModule,
    LoaderComponent,
    MatError,
  ],
  templateUrl: './manual-appointment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ManualAppointmentFormComponent),
      multi: true,
    },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ManualAppointmentFormComponent), multi: true },
  ],
})
export class ManualAppointmentFormComponent implements OnInit, ControlValueAccessor, Validator {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly visitsService = inject(VisitsService);

  protected readonly appointmentControlsNames = AppointmentControlsNames;

  private onTouched!: () => void;
  private onChange!: (value: Partial<AppointmentValue> | null) => void;

  readonly type = input.required<ModalActionTypes>();
  readonly action = input<Visit | null>(null);
  readonly appointment = input<Visit | null>(null);

  protected appointmentForm: FormGroup<AppointmentControls>;

  protected readonly loading = signal<boolean>(true);
  protected readonly allCitiesOptions = signal<string[]>([]);
  protected readonly filteredCitiesOptions = signal<string[]>([]);
  protected readonly allPartnersOptions = signal<Partner[]>([]);
  protected readonly allLocationsOptions = signal<PartnerLocation[]>([]);

  protected get fc(): AppointmentControls {
    return this.appointmentForm?.controls;
  }

  ngOnInit() {
    this.initForm();
  }

  private watchCityChanges(): void {
    this.appointmentForm.controls[this.appointmentControlsNames.City].valueChanges.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe((city: string) => {
      this.clearCityRelatedControls();
      if (city) {
        this.filteredCitiesOptions.set(this.allCitiesOptions().filter(c => c?.toLowerCase().includes(city?.toLowerCase())));
        if (this.filteredCitiesOptions().includes(city)) {
          this.searchPartners(city).subscribe();
        }
      } else {
        this.filteredCitiesOptions.set(this.allCitiesOptions());
      }
    });
  }

  protected selectPartnerOption(partnerId: string): void {
    this.clearPartnerRelatedControls();
    this.fc[this.appointmentControlsNames.PartnerId].setValue(partnerId);
    this.searchPartnersLocations(partnerId, this.appointmentForm.controls[this.appointmentControlsNames.City].value, null).subscribe();
  }

  protected selectLocationOption(locationId: string): void {
    this.fc[this.appointmentControlsNames.LocationId].setValue(locationId);
  }

  private getPartnersCities() {
    return this.visitsService.getPartnersCities().pipe(
      tap((cities: string[]) => {
        this.allCitiesOptions.set(cities);
        this.filteredCitiesOptions.set(this.allCitiesOptions());
      }),
      take(1)
    );
  }

  private searchPartners(city: string) {
    const searchRequest: PartnerSearchPayload = {
      page: 1,
      pageSize: 500,
      city: city,
      status: 'Active',
    };
    return this.visitsService.searchPartners(searchRequest).pipe(
      tap((page: PagedResponse<Partner>) => {
        this.allPartnersOptions.set(page.value);
      }),
      take(1)
    );
  }

  private searchPartnersLocations(partnerId: string, city: string | null, locationId: string | null) {
    const searchRequest: PartnerLocationsSearchPayload = {
      page: 1,
      pageSize: 500,
      city: city,
      locationId: locationId,
    };
    return this.visitsService.searchPartnersLocations(partnerId, searchRequest).pipe(
      tap(locations => {
        this.allLocationsOptions.set(locations.value.filter(location => location.status === 'Active'));
      }),
      take(1)
    );
  }

  private clearCityRelatedControls(): void {
    this.fc[this.appointmentControlsNames.PartnerId].setValue(null, { emitEvent: false });
    this.fc[this.appointmentControlsNames.PartnerName].setValue(null, { emitEvent: false });
    this.fc[this.appointmentControlsNames.LocationId].setValue(null, { emitEvent: false });
    this.fc[this.appointmentControlsNames.Location].setValue(null, { emitEvent: false });
    this.allPartnersOptions.set([]);
    this.allLocationsOptions.set([]);
  }

  private clearPartnerRelatedControls(): void {
    this.fc[this.appointmentControlsNames.LocationId].setValue(null, { emitEvent: false });
    this.fc[this.appointmentControlsNames.Location].setValue(null, { emitEvent: false });
    this.allLocationsOptions.set([]);
  }

  private initForm(): void {
    this.loading.set(true);
    const prescriptions = this.action()?.prescriptions || this.appointment()?.prescriptions || [];
    this.appointmentForm = this.fb.group<AppointmentControls>({
      [this.appointmentControlsNames.PrescriptionsCodes]: this.fb.control(prescriptions.map(prescription => ({ code: prescription.serviceCode, name: prescription.name }))),
      // TODO: Remove next line when sure that prescriptions ids array is obsolete.
      [this.appointmentControlsNames.PrescriptionIds]: this.fb.control(prescriptions.map(prescription => prescription.id)),
      [this.appointmentControlsNames.AppointmentType]: this.fb.control(this.type() === ModalActionTypes.Create ? this.action().metadata.type : this.appointment().metadata.type, [
        Validators.required,
      ]),
      [this.appointmentControlsNames.Name]: this.fb.control(this.type() === ModalActionTypes.Create ? this.action().metadata.name : this.appointment().metadata.name, [
        Validators.required,
      ]),
      [this.appointmentControlsNames.Price]: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().price, [Validators.required]),
      [this.appointmentControlsNames.DoctorName]: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().metadata.doctor_name, [Validators.required]),
      [this.appointmentControlsNames.DateStart]: this.fb.control(this.type() === ModalActionTypes.Create ? null : new Date(this.appointment().metadata.appointment_full_date), [
        Validators.required,
      ]),
      [this.appointmentControlsNames.Time]: this.fb.control(
        this.type() === ModalActionTypes.Create ? null : this.getTime(new Date(this.appointment().metadata.appointment_full_date)),
        [Validators.required]
      ),
      // TODO: Change setting empty guid for null for the next line when integration with partners admin is complete, and add selection of partners from api.
      [this.appointmentControlsNames.PartnerId]: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().partnerId, [Validators.required]),
      [this.appointmentControlsNames.PartnerName]: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().metadata.partner_name, [
        Validators.required,
      ]),
      // TODO: Change setting empty guid for null for the next line when integration with partners admin is complete, and add selection of partners from api.
      [this.appointmentControlsNames.LocationId]: this.fb.control(this.type() === ModalActionTypes.Create ? null : null, [Validators.required]),
      [this.appointmentControlsNames.Location]: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().metadata.location, [Validators.required]),
      [this.appointmentControlsNames.AppointmentEventPlaceType]: this.fb.control(
        this.type() === ModalActionTypes.Create ? AppointmentEventPlaceType.Hospital : this.appointment().metadata.event_place,
        [Validators.required]
      ),
      [this.appointmentControlsNames.City]: this.fb.control(null, [Validators.required]),
      [this.appointmentControlsNames.AppointmentCompletedFlag]: this.fb.control(
        this.type() === ModalActionTypes.Create ? false : this.appointment().status === VisitStatusTypes.Completed
      ),
    });

    this.watchCityChanges();
    if (this.type() === ModalActionTypes.Create) {
      this.getPartnersCities().subscribe(() => {
        this.watchFormChanges();
        this.loading.set(false);
      });
    }
    if (this.type() === ModalActionTypes.Edit) {
      this.initAsyncEditData().subscribe(() => {
        this.watchFormChanges();
        this.loading.set(false);
      });
    }
    if (this.type() === ModalActionTypes.Delete) {
      this.loading.set(false);
    }
  }

  private initAsyncEditData() {
    const currentPartnerId = this.appointment().partnerId;
    const currentLocationId = this.appointment().systemMetadata.location_id;
    return this.searchPartnersLocations(currentPartnerId, null, currentLocationId).pipe(
      switchMap(locations => {
        const currentLocation = locations.value.find(location => location.id === currentLocationId && location.status === 'Active');
        if (!currentLocation) {
          return EMPTY;
        }
        this.fc[this.appointmentControlsNames.City].setValue(currentLocation.city, { emitEvent: false });
        return this.getPartnersCities();
      }),
      switchMap(() => this.searchPartners(this.fc[this.appointmentControlsNames.City].value)),
      tap(partners => {
        const currentPartner = partners.value.find(partner => partner.id === currentPartnerId);
        if (currentPartner) {
          this.fc[this.appointmentControlsNames.PartnerId].setValue(currentPartner.id, { emitEvent: false });
          this.fc[this.appointmentControlsNames.PartnerName].setValue(currentPartner.shortName, { emitEvent: false });
        }
      }),
      switchMap(() => this.searchPartnersLocations(currentPartnerId, this.fc[this.appointmentControlsNames.City].value, null)),
      tap(locations => {
        const currentLocation = locations.value.find(location => location.id === currentLocationId && location.status === 'Active');
        if (currentLocation) {
          this.fc[this.appointmentControlsNames.LocationId].setValue(currentLocation.id, { emitEvent: false });
          this.fc[this.appointmentControlsNames.Location].setValue(currentLocation.address, { emitEvent: false });
        }
      }),
      take(1)
    );
  }

  private watchFormChanges(): void {
    this.appointmentForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.onChange(value);
    });

    this.appointmentForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.onTouched();
    });
  }

  private getTime(date: Date): string {
    const hours = date.getHours() < 10 ? '0' + date.getHours() : date.getHours().toString();
    const minutes = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
    return `${hours}:${minutes}`;
  }

  writeValue(value: unknown): void {
    if (this.appointmentForm && value) {
      this.appointmentForm.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.appointmentForm.disable();
    } else {
      this.appointmentForm.enable();
    }
  }

  validate(): ValidationErrors | null {
    return this.appointmentForm.valid ? null : { invalidAppointmentForm: true };
  }
}
